import { getAgentData, useAgentSelector } from "@/modules/agent/states";
import { ConfidenceTypes } from "@/modules/agent/types";
import {
  VendorPotentialIssueItem,
} from "@/modules/agent/types/vendorPotentialIssues";
import { useAgentSocket } from "@/modules/agent/utils/agent-socket";
import { AgentSessionStepType } from "@/modules/sessions/types";
import { ZButton } from "@/shared/components/button";
import { ZInput, ZTextarea } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import { addNotification } from "@/shared/states/notification";
import { Select, SelectItem } from "@nextui-org/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { IdParams } from "../Overview/Overview";
import { useVendorRiskAssessmentStore } from "../useVendorRiskAssessment/useVendorRiskAssessmentStore";

// Type definition for the potential issue structure
interface PotentialIssueItem {
  gap?: {
    value: string;
    severity?: string;
    sources?: Array<unknown>;
  };
  recommendation?: {
    value: string;
    sources?: Array<unknown>;
  };
  severity?: string;
  confidence?: ConfidenceTypes;
  sources?: Array<unknown>;
  control_title?: string;
}

// Extend vendor risk response item to support the structure
interface ExtendedVendorRiskResponseItem {
  section: string;
  key: string;
  question: string;
  value: {
    type: string;
    value: VendorPotentialIssueItem[];
    potential_issues: PotentialIssueItem[];
  };
  id?: string;
}

export type IAddPotentialIssuePopupProps =
  | {
    handleClose: () => void;
    type: "EDIT";
    editKey: string;
    editIdx: number;
  }
  | {
    handleClose: () => void;
    type: "ADD";
  };

const AddPotentialIssuePopup = (props: IAddPotentialIssuePopupProps) => {
  const { id } = useParams<keyof IdParams>() as IdParams;
  const { handleClose, type } = props;
  
  // Extract editKey and editIdx if in EDIT mode
  const editKey = type === "EDIT" ? (props as { editKey: string }).editKey : "";
  const editIdx = type === "EDIT" ? (props as { editIdx: number }).editIdx : -1;
  
  const [selectedIssueType, setSelectedIssueType] = useState(type === "ADD" ? "security" : "");
  const [gapValue, setGapValue] = useState("");
  const [recommendationValue, setRecommendationValue] = useState("");
  const [severity, setSeverity] = useState("");
  const [controlTitle, setControlTitle] = useState("");
  const { setVendorPotentialIssues, vendorPotentialIssuesData } =
    useVendorRiskAssessmentStore.getState();
  const { setSelectedId } = useAgentSelector.useActions();
  const { emitControlEdit } = useAgentSocket();
  
  // Track if form values have changed for autosave
  const [initialValues, setInitialValues] = useState({
    controlTitle: '',
    gapValue: '',
    recommendationValue: '',
    severity: '',
    selectedIssueType: type === "ADD" ? "security" : "",
  });
  
  // Track if form is valid for autosave
  const isFormValid = useMemo(() => {
    return (
      selectedIssueType.length > 0 &&
      controlTitle.length > 0 &&
      gapValue.length > 0 &&
      recommendationValue.length > 0 &&
      severity.length > 0 &&
      controlTitle.length > 0
    );
  }, [selectedIssueType, gapValue, recommendationValue, severity, controlTitle]);

  useEffect(() => {
    if (type === "EDIT") {
      // Always use the container at index 0 which has the potential_issues
      const potentialIssuesContainer = vendorPotentialIssuesData[0] as ExtendedVendorRiskResponseItem;

      if (potentialIssuesContainer?.value?.potential_issues) {
        // In edit mode, we don't care about selectedIssueType - everything is in one container
        setSelectedIssueType(potentialIssuesContainer.key);
        
        const issueItem = potentialIssuesContainer.value.potential_issues[editIdx];
        if (issueItem) {
          const newControlTitle = issueItem.control_title || "";
          const newGapValue = issueItem.gap?.value || "";
          const newRecommendationValue = issueItem.recommendation?.value || "";
          const newSeverity = issueItem.severity || "";
          
          setControlTitle(newControlTitle);
          setGapValue(newGapValue);
          setRecommendationValue(newRecommendationValue);
          setSeverity(newSeverity);
          setControlTitle(newControlTitle);
          
          // Set initial values for change detection
          setInitialValues({
            controlTitle: newControlTitle,
            gapValue: newGapValue,
            recommendationValue: newRecommendationValue,
            severity: newSeverity,
            selectedIssueType: potentialIssuesContainer.key,
          });
        } else {
          handleClose();
        }
      } else {
        handleClose();
      }
    }
  }, [handleClose, type, editIdx, vendorPotentialIssuesData]);

  const buttonDisabled = useMemo(() => {
    return (
      selectedIssueType.length === 0 ||
      controlTitle.length === 0 ||
      gapValue.length === 0 ||
      recommendationValue.length === 0 ||
      severity.length === 0 ||
      controlTitle.length === 0
    )
  }, [selectedIssueType.length, gapValue.length, recommendationValue.length, severity.length, controlTitle.length]);

  const handleSubmitClick = useCallback(() => {
    const temp = [...vendorPotentialIssuesData] as ExtendedVendorRiskResponseItem[];
    
    // Get the container at index 0 which already has potential_issues
    const potentialIssuesContainer = temp[0] as ExtendedVendorRiskResponseItem;
    
    if (!potentialIssuesContainer?.value?.potential_issues) {
      console.error("potentialIssuesContainer not found or missing potential_issues array");
      return;
    }

    if (type === "ADD") {
      // Check for duplicates in potential_issues array
      const isDuplicate = potentialIssuesContainer.value.potential_issues.some(item => {
        return item.gap?.value.toLowerCase().trim() === gapValue.toLowerCase().trim() &&
          item.recommendation?.value.toLowerCase().trim() === recommendationValue.toLowerCase().trim();
      });

      if (isDuplicate) {
        addNotification({
          type: "error",
          message: "This issue already exists"
        });
        return;
      }

      // Create the new issue with the structure expected by the application
      const newIssue = {
        control_title: controlTitle,
        gap: {
          value: gapValue,
          sources: []
        },
        recommendation: {
          value: recommendationValue,
          sources: []
        },
        severity: severity,
        confidence: ConfidenceTypes.DNF,
        sources: [],
      };

      // Add to the beginning of the potential_issues array so it appears at the top of the list
      potentialIssuesContainer.value.potential_issues.unshift(newIssue);
      
      // Set selectedId for UI highlighting - index is 0 since we added it to the beginning
      setSelectedId(id, `${potentialIssuesContainer.key};0`);
    } else {
      // Edit mode - already confirmed container exists above
      if (potentialIssuesContainer.value.potential_issues) {
        // Always edit the existing item at the specified index
        potentialIssuesContainer.value.potential_issues[editIdx] = {
          control_title: controlTitle,
          gap: {
            value: gapValue,
            sources: []
          },
          recommendation: {
            value: recommendationValue,
            sources: []
          },
          severity: severity,
          confidence: potentialIssuesContainer.value.potential_issues[editIdx].confidence,
          sources: potentialIssuesContainer.value.potential_issues[editIdx].sources || [],
        };
        
        // Set selectedId with the index
        setSelectedId(id, `${potentialIssuesContainer.key};${editIdx}`);
      }
    }

    // Update the store, but prevent triggering the autosave with dontIncrementChangesMade=true
    setVendorPotentialIssues(temp as any, true);
    
    // Manually trigger the socket event for immediate update
    const agentData = getAgentData(id);
    if (agentData) {
      const stepData = agentData.stepData;
      const editStepData = stepData?.find(
        (step) => step.type === AgentSessionStepType.EDIT_RESPONSE
      );
      
      if (editStepData?.data?.url) {
        // Send the socket event directly
        emitControlEdit(
          editStepData.data.url,
          potentialIssuesContainer,
          potentialIssuesContainer.key,
          id,
          editStepData.id,
          "potential_issues"
        );
      }
    }
    
    addNotification({
      type: "success",
      message:
        type === "ADD"
          ? "New potential issue added successfully"
          : "Potential issue updated successfully",
    });

    // Always close the modal after submitting
    handleClose();
  }, [
    id, 
    controlTitle,
    gapValue, 
    recommendationValue, 
    severity,
    controlTitle,
    vendorPotentialIssuesData, 
    type,
    handleClose, 
    setSelectedId,
    setVendorPotentialIssues,
    editIdx,
    emitControlEdit
  ]);
  
  // Detect changes and trigger autosave
  useEffect(() => {
    // Completely disable auto-saving as it's causing issues
    // Auto-saving should only happen after explicit submit button click
    return;
  }, []);

  return (
    <ZModal
      isOpen={true}
      onOpenChange={() => {
        handleClose();
      }}
      aria-label="Add Potential Issue"
      className="max-h-[calc(100vh-150px)] overflow-y-auto"
    >
      <ZModalContent className="max-w-lg flex flex-col gap-6">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-0">
          <p>{type === "ADD" ? "Add" : "Edit"} Issue</p>
        </ZModalHeader>

        <ZModalBody className="pt-0 overflow-auto flex gap-8">

          <ZInput
            label="Control Title"
            labelPlacement="outside"
            placeholder="Add control title"
            value={controlTitle}
            onChange={(e) => setControlTitle(e.target.value.trimStart())}
            className="w-full rounded-sm bg-white"
          />

          <ZTextarea
            label="Gap"
            labelPlacement="outside"
            placeholder="Add gap details"
            value={gapValue}
            onChange={(e) => setGapValue(e.target.value.trimStart())}
            className="w-full rounded-sm bg-white"
          />
          
          <Select
            items={[
              { key: "Low", label: "Low" },
              { key: "Medium", label: "Medium" },
              { key: "High", label: "High" },
              { key: "Critical", label: "Critical" },
            ]}
            label={"Severity"}
            labelPlacement="outside"
            value={severity}
            placeholder="Select Severity"
            onChange={(value) => {
              setSeverity(value.target.value);
            }}
            selectedKeys={
              severity ? [severity] : []
            }
          >
            {(item) => (
              <SelectItem key={item.key} value={item.key}>
                {item.label}
              </SelectItem>
            )}
          </Select>
          
          <ZTextarea
            label="Recommendation"
            labelPlacement="outside"
            placeholder="Add recommendation"
            value={recommendationValue}
            onChange={(e) => setRecommendationValue(e.target.value.trimStart())}
            className="w-full rounded-sm bg-white"
          />
        </ZModalBody>
        <ZModalFooter className="flex gap-4">
          <ZButton
            variant="bordered"
            onClick={() => handleClose()}
            className="px-3 py-1 text-medium flex-1"
          >
            Discard
          </ZButton>
          <ZButton
            type="submit"
            onClick={handleSubmitClick}
            isDisabled={buttonDisabled}
            className="px-3 py-1 text-medium flex-1"
          >
            Submit
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
};

export default AddPotentialIssuePopup; 